// @ts-nocheck
export const haversineMiles = (lat1, lng1, lat2, lng2) => {
  const toRad = (value) => (value * Math.PI) / 180;
  const earthRadiusMiles = 3958.8;
  const dLat = toRad(lat2 - lat1);
  const dLng = toRad(lng2 - lng1);
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) *
      Math.sin(dLng / 2) * Math.sin(dLng / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return earthRadiusMiles * c;
};

const DAY_MS = 24 * 60 * 60 * 1000;

const normalizeConventionToken = (value) =>
  String(value || "")
    .toLowerCase()
    .replace(/\bday\s*\d+\b/g, "")
    .replace(/[^\w\s]/g, " ")
    .replace(/\s+/g, " ")
    .trim();

const buildGroupingKey = (event) => {
  const websiteKey = String(event.website || event.url || "").trim().toLowerCase();
  const nameKey = normalizeConventionToken(event.name);
  const venueKey = normalizeConventionToken(event.venueName);
  const cityKey = normalizeConventionToken(event.city);
  const stateKey = normalizeConventionToken(event.state);
  return [websiteKey || nameKey, venueKey, cityKey, stateKey].join("|");
};

const areDatesContiguous = (leftIso, rightIso) => {
  const left = new Date(leftIso).getTime();
  const right = new Date(rightIso).getTime();
  if (!Number.isFinite(left) || !Number.isFinite(right)) return false;
  return right - left <= DAY_MS * 1.5;
};

export const groupEventsForDisplay = (events = []) => {
  const sorted = [...events].sort(
    (a, b) => new Date(a.startDateTime || a.startDate || 0).getTime() - new Date(b.startDateTime || b.startDate || 0).getTime(),
  );

  const groups = [];
  for (const event of sorted) {
    const sourceId = String(event.primaryId || event.id);
    const sourceIds = Array.isArray(event.sourceIds) && event.sourceIds.length > 0 ? event.sourceIds : [sourceId];
    const key = buildGroupingKey(event);
    const startIso = event.startDateTime || event.startDate;
    const endIso = event.endDateTime || event.endDate || startIso;
    const lastGroup = groups[groups.length - 1];

    if (
      lastGroup &&
      lastGroup.groupKey === key &&
      areDatesContiguous(lastGroup.groupedEndDate || lastGroup.endDateTime, startIso)
    ) {
      lastGroup.sourceIds = Array.from(new Set([...(lastGroup.sourceIds || []), ...sourceIds]));
      lastGroup.games = Array.from(new Set([...(lastGroup.games || []), ...(event.games || [])]));
      lastGroup.groupedEndDate = endIso;
      lastGroup.endDate = endIso;
      lastGroup.endDateTime = endIso;
      lastGroup.dayCount = Math.max(lastGroup.dayCount || 1, lastGroup.sourceIds.length);
      continue;
    }

    groups.push({
      ...event,
      id: sourceId,
      primaryId: sourceId,
      sourceIds,
      groupedStartDate: startIso,
      groupedEndDate: endIso,
      startDate: startIso,
      endDate: endIso,
      startDateTime: startIso,
      endDateTime: endIso,
      dayCount: sourceIds.length,
      groupKey: key,
    });
  }

  return groups.map(({ groupKey, ...event }) => event);
};

export const formatEventDate = (isoString) => {
  const date = new Date(isoString);
  return date.toLocaleDateString("en-US", {
    weekday: "short",
    month: "short",
    day: "numeric",
  });
};

export const formatEventTime = (isoString) => {
  const date = new Date(isoString);
  return date.toLocaleTimeString("en-US", {
    hour: "numeric",
    minute: "2-digit",
  });
};

export const formatDateRangeLabel = (startIso, endIso) => {
  if (!startIso && !endIso) {
    return "Date TBD";
  }
  if (!endIso) {
    endIso = startIso;
  }
  if (!startIso) {
    startIso = endIso;
  }
  const startDate = new Date(startIso);
  const endDate = new Date(endIso);
  const startLabel = startDate.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
  const endLabel = endDate.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
  if (startLabel === endLabel) {
    return startLabel;
  }
  return `${startLabel} - ${endLabel}`;
};

export const getDateRange = (rangeKey) => {
  const now = new Date();
  const start = new Date(now);
  const end = new Date(now);

  if (rangeKey === "today") {
    end.setHours(23, 59, 59, 999);
    return { start, end };
  }

  if (rangeKey === "this_week") {
    const day = now.getDay();
    const diff = now.getDate() - day + 7;
    end.setDate(diff);
    end.setHours(23, 59, 59, 999);
    return { start, end };
  }

  if (rangeKey === "this_month") {
    end.setMonth(now.getMonth() + 1, 0);
    end.setHours(23, 59, 59, 999);
    return { start, end };
  }

  return { start: null, end: null };
};

export const isEventInRange = (event, rangeKey, customRange) => {
  if (!rangeKey || rangeKey === "all") {
    return true;
  }

  const eventStart = new Date(event.groupedStartDate || event.startDateTime);
  const eventEnd = new Date(event.groupedEndDate || event.endDateTime || event.startDateTime);

  if (rangeKey === "custom") {
    if (!customRange?.start || !customRange?.end) {
      return true;
    }
    const start = new Date(customRange.start);
    const end = new Date(customRange.end);
    end.setHours(23, 59, 59, 999);
    return eventStart <= end && eventEnd >= start;
  }

  const { start, end } = getDateRange(rangeKey);
  if (!start || !end) {
    return true;
  }
  return eventStart <= end && eventEnd >= start;
};

export const formatLocationLabel = (location) => {
  if (!location) {
    return "";
  }
  return location.label;
};

export const formatEventTypeLabel = (eventType) => {
  if (!eventType) return "";
  return String(eventType)
    .toLowerCase()
    .split("_")
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
};
